import React from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'

export interface EditProfileOption {
    value: string
    label: string
}

export interface EditProfileFormValues {
    title: string
    firstName: string
    lastName: string
    gender: string
    maritalStatus: string
    dateOfBirth: string
    marketingSubscription: boolean
}

interface EditProfileProps {
    values: EditProfileFormValues
    t: (key: string) => string
    titleOptions: EditProfileOption[]
    genderOptions: EditProfileOption[]
    maritalStatusOptions: EditProfileOption[]
    isSaving?: boolean
    saveError?: string | null
    /** Hide the in-form heading when the surface already shows the title (e.g. mobile header). */
    hideHeading?: boolean
    onChange: (patch: Partial<EditProfileFormValues>) => void
    onCancel: () => void
    onSave: () => void
}

const EditProfile: React.FC<EditProfileProps> = ({
    values,
    t,
    titleOptions,
    genderOptions,
    maritalStatusOptions,
    isSaving,
    saveError,
    hideHeading,
    onChange,
    onCancel,
    onSave
}) => {
    return (
        <Box sx={{ maxWidth: 850 }}>
            {!hideHeading && (
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        lineHeight: 1.3,
                        color: 'text.primary',
                        mb: { xs: 2, sm: 2.5 }
                    }}
                >
                    {t('editTitle')}
                </Typography>
            )}

            <Card elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}>
                <CardContent sx={{ p: { xs: 2.5 } }}>
                    <Stack spacing={2} gap={1.5}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ width: { xs: 110, md: 140 } }}>
                                <FormControl fullWidth>
                                    <InputLabel id="edit-profile-title-label">{t('titleLabel')}</InputLabel>
                                    <Select
                                        labelId="edit-profile-title-label"
                                        value={values.title}
                                        label={t('titleLabel')}
                                        onChange={e => onChange({ title: e.target.value })}
                                    >
                                        {titleOptions.map(opt => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: { xs: 0, md: 200 } }}>
                                <TextField
                                    label={t('firstName')}
                                    value={values.firstName}
                                    onChange={e => onChange({ firstName: e.target.value })}
                                    fullWidth
                                />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}>
                                <TextField
                                    label={t('lastName')}
                                    value={values.lastName}
                                    onChange={e => onChange({ lastName: e.target.value })}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel id="edit-profile-gender-label">{t('gender')}</InputLabel>
                            <Select
                                labelId="edit-profile-gender-label"
                                value={values.gender}
                                label={t('gender')}
                                onChange={e => onChange({ gender: e.target.value })}
                            >
                                {genderOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel id="edit-profile-marital-label">{t('maritalStatus')}</InputLabel>
                            <Select
                                labelId="edit-profile-marital-label"
                                value={values.maritalStatus}
                                label={t('maritalStatus')}
                                onChange={e => onChange({ maritalStatus: e.target.value })}
                            >
                                {maritalStatusOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label={t('dateOfBirth')}
                            type="date"
                            value={values.dateOfBirth}
                            onChange={e => onChange({ dateOfBirth: e.target.value })}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={values.marketingSubscription}
                                    onChange={e => onChange({ marketingSubscription: e.target.checked })}
                                />
                            }
                            label={t('marketingSubscription')}
                            sx={{ color: 'text.secondary' }}
                        />

                        {saveError && (
                            <Alert severity="error" sx={{ borderRadius: 1 }}>
                                {saveError}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'right', gap: 2, pt: { xs: 1, sm: 1.5 } }}>
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                                disabled={isSaving}
                                sx={{ textTransform: 'none', minWidth: 140, fontWeight: 600, py: 1.5 }}
                            >
                                {t('cancel')}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={onSave}
                                disabled={isSaving}
                                sx={{ textTransform: 'none', minWidth: 160, fontWeight: 600, py: 1.5 }}
                            >
                                {isSaving ? <CircularProgress size={22} color="inherit" /> : t('saveChanges')}
                            </Button>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    )
}

export { EditProfile }
export type { EditProfileProps }
